import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Settings, User as UserIcon, CreditCard, Save, Loader2, Sparkles, ExternalLink, CalendarClock, Plug, AlertTriangle, Download, Trash2, ShieldCheck, Clock, FileText, ArrowDownToLine, LifeBuoy, Search, Webhook, Palette, Bot, MessageSquare } from "lucide-react";
import { AiAgentPanel } from "@/components/AiAgentPanel";
import { AgentChannelsPanel } from "@/components/AgentChannelsPanel";
import { WebhooksPanel } from "@/components/WebhooksPanel";
import { WhiteLabelPanel } from "@/components/WhiteLabelPanel";
import { MetaIntegrationCard } from "@/components/MetaIntegrationCard";
import { TikTokIntegrationCard } from "@/components/TikTokIntegrationCard";
import { GoogleIntegrationCard } from "@/components/GoogleIntegrationCard";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useI18n } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { getStripeEnvironment } from "@/lib/stripe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { exportUserData, deleteUserAccount } from "@/utils/account.functions";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "INR", "BRL", "MXN", "CHF"] as const;

export const Route = createFileRoute("/_app/settings")({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    ...(typeof search.tab === "string" ? { tab: search.tab } : {}),
  }),
  component: SettingsPage,
  head: () => ({
    meta: [
      { title: "Settings · AdKavi" },
      { name: "description", content: "Manage your profile, currency, and billing." },
    ],
  }),
});

function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { tab: initialTab } = Route.useSearch();
  const [displayName, setDisplayName] = useState("");
  const [currency, setCurrency] = useState<string>("USD");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("display_name, preferred_currency")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error(error);
        setDisplayName(data?.display_name ?? "");
        setCurrency(data?.preferred_currency ?? "USD");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim() || null, preferred_currency: currency })
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error(t("settings.toast.saveFailed"), { description: error.message });
    } else {
      toast.success(t("settings.toast.profileUpdated"));
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Settings className="w-8 h-8 text-orange-400" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">
            {t("settings.title")}
          </span>
        </h1>
        <p className="mt-2 text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <Tabs defaultValue={initialTab ?? "profile"} className="w-full">
        <div className="border-b border-border/50 mb-6">
          <TabsList className="flex flex-wrap gap-0.5 h-auto w-full max-w-3xl justify-start bg-transparent p-0 pb-0">
            {(
              [
                { value: "profile", icon: <UserIcon className="w-4 h-4" />, label: t("settings.tab.profile") },
                { value: "ai-agent", icon: <Bot className="w-4 h-4" />, label: t("settings.tab.aiAgent") },
                { value: "channels", icon: <MessageSquare className="w-4 h-4" />, label: t("settings.tab.channels") },
                { value: "branding", icon: <Palette className="w-4 h-4" />, label: t("whitelabel.tab") },
                { value: "integrations", icon: <Plug className="w-4 h-4" />, label: t("settings.tab.integrations") },
                { value: "webhooks", icon: <Webhook className="w-4 h-4" />, label: t("webhooks.tab") },
                { value: "controls", icon: <ShieldCheck className="w-4 h-4" />, label: t("settings.tab.controls") },
                { value: "billing", icon: <CreditCard className="w-4 h-4" />, label: t("settings.tab.billing") },
              ] as const
            ).map(({ value, icon, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="relative rounded-none rounded-t-md px-3.5 py-2 text-sm gap-1.5 text-muted-foreground border-b-2 border-transparent data-[state=active]:border-orange-400 data-[state=active]:text-foreground data-[state=active]:bg-orange-400/5 hover:text-foreground transition-colors"
              >
                {icon} {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.tab.profile")}</CardTitle>
              <CardDescription>{t("settings.profile.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">{t("settings.profile.email")}</Label>
                <Input id="email" value={user?.email ?? ""} disabled />
                <p className="text-xs text-muted-foreground">
                  {t("settings.profile.emailHint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName">{t("settings.profile.displayName")}</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t("settings.profile.displayNamePlaceholder")}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">{t("settings.profile.currency")}</Label>
                <Select value={currency} onValueChange={setCurrency} disabled={loading}>
                  <SelectTrigger id="currency" className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("settings.profile.currencyHint")}
                </p>
              </div>

              <LanguageSetting />

              <div className="pt-2">
                <Button onClick={saveProfile} disabled={saving || loading} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> {t("settings.profile.save")}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-agent">
          <AiAgentPanel />
        </TabsContent>

        <TabsContent value="channels">
          <AgentChannelsPanel />
        </TabsContent>

        <TabsContent value="branding">
          <WhiteLabelPanel />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <MetaIntegrationCard />
          <TikTokIntegrationCard />
          <GoogleIntegrationCard />
        </TabsContent>

        <TabsContent value="webhooks">
          <WebhooksPanel />
        </TabsContent>

        <TabsContent value="controls">
          <ComplianceControlsPanel />
        </TabsContent>

        <TabsContent value="billing">
          <BillingPanel />
        </TabsContent>
      </Tabs>

      <DangerZone />
    </section>
  );
}

function LanguageSetting() {
  const { t } = useI18n();
  return (
    <div className="space-y-2 pt-2 border-t border-border/40">
      <Label>{t("settings.language")}</Label>
      <LanguageSwitcher variant="full" />
      <p className="text-xs text-muted-foreground">{t("settings.languageDesc")}</p>
    </div>
  );
}

function DangerZone() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const exportFn = useServerFn(exportUserData);
  const deleteFn = useServerFn(deleteUserAccount);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const data = await exportFn();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adkavi-data-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings.toast.dataDownloaded"));
    } catch (e: any) {
      toast.error(t("settings.toast.downloadFailed"), { description: e?.message });
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    try {
      await deleteFn({ data: { confirm: "DELETE" } });
      await supabase.auth.signOut();
      toast.success(t("settings.toast.accountDeleted"));
      navigate({ to: "/", search: { deleted: "1" } as any });
    } catch (e: any) {
      setDeleting(false);
      toast.error(t("settings.toast.accountDeleteFailed"), { description: e?.message });
    }
  };

  return (
    <>
      <Card className="mt-10 border-destructive/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" /> {t("settings.danger.title")}
          </CardTitle>
          <CardDescription>{t("settings.danger.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-border/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-semibold">{t("settings.danger.downloadTitle")}</h3>
              <p className="text-sm text-muted-foreground">{t("settings.danger.downloadDesc")}</p>
            </div>
            <Button variant="outline" onClick={handleDownload} disabled={downloading}>
              {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("settings.danger.preparing")}</> : <><Download className="w-4 h-4 mr-2" /> {t("settings.danger.downloadBtn")}</>}
            </Button>
          </div>

          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-semibold">{t("settings.danger.deleteTitle")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("settings.danger.deleteDesc")}
              </p>
            </div>
            <Button variant="destructive" onClick={() => setOpen(true)}>
              <Trash2 className="w-4 h-4 mr-2" /> {t("settings.danger.deleteBtn")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { if (!deleting) { setOpen(v); if (!v) setConfirmText(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> {t("settings.danger.confirmTitle")}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm">
                <p>{t("settings.danger.confirmWhat")}</p>
                <ul className="list-disc pl-5 space-y-1 text-foreground/80">
                  <li>{t("settings.danger.confirmList1")}</li>
                  <li>{t("settings.danger.confirmList2")}</li>
                  <li>{t("settings.danger.confirmList3")}</li>
                  <li>{t("settings.danger.confirmList4")}</li>
                </ul>
                <p className="pt-2">{t("settings.danger.confirmTypePre")} <span className="font-mono font-bold text-foreground">DELETE</span> {t("settings.danger.confirmTypePost")}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            autoFocus
            disabled={deleting}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setOpen(false); setConfirmText(""); }} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={confirmText !== "DELETE" || deleting}>
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("settings.danger.deleting")}</> : t("settings.danger.permanentlyDelete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BillingPanel() {
  const { t } = useI18n();
  const { subscription, plan, isActive, loading } = useSubscription();
  const [opening, setOpening] = useState(false);

  const openPortal = async () => {
    setOpening(true);
    const { data, error } = await supabase.functions.invoke("create-portal-session", {
      body: {
        environment: getStripeEnvironment(),
        returnUrl: `${window.location.origin}/settings`,
      },
    });
    setOpening(false);
    if (error || !data?.url) {
      toast.error(t("settings.toast.portalFailed"), { description: error?.message ?? t("common.tryAgain") });
      return;
    }
    window.open(data.url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t("settings.billing.loading")}
        </CardContent>
      </Card>
    );
  }

  if (!subscription || !plan || !isActive) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.billing.noActivePlan")}</CardTitle>
          <CardDescription>{t("settings.billing.noActivePlanDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-dashed border-border/60 bg-background/30 p-8 text-center">
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-lg">{t("settings.billing.startTrialTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              {t("settings.billing.startTrialDesc")}
            </p>
            <Button asChild className="mt-5 rounded-full bg-orange-500 hover:bg-orange-600 text-white border-0">
              <Link to="/pricing">{t("settings.billing.viewPlans")}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
  const trialEnd = subscription.trial_end ? new Date(subscription.trial_end) : null;
  const isTrialing = subscription.status === "trialing";

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              {plan.name}
              <Badge variant={isTrialing ? "secondary" : "default"} className="capitalize">
                {subscription.status}
              </Badge>
            </CardTitle>
            <CardDescription>
              ${plan.price.toLocaleString()}/month
              {subscription.cancel_at_period_end && ` ${t("settings.billing.cancelAtPeriodEnd")}`}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isTrialing && trialEnd && (
          <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
            <CalendarClock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">
                {t("settings.billing.trialEnds")} {trialEnd.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </p>
              <p className="text-muted-foreground mt-0.5">
                {t("settings.billing.trialChargeWarning").replace("{price}", plan.price.toLocaleString())}
              </p>
            </div>
          </div>
        )}
        {!isTrialing && periodEnd && (
          <div className="text-sm text-muted-foreground">
            {subscription.cancel_at_period_end ? t("settings.billing.accessEnds") : t("settings.billing.renews")} {t("settings.billing.on")}{" "}
            {periodEnd.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.
          </div>
        )}
        <ul className="space-y-1.5 text-sm text-muted-foreground">
          {plan.features.map((f) => <li key={f}>— {f}</li>)}
        </ul>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={openPortal} disabled={opening}>
            {opening ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("settings.billing.opening")}</>) : (<><ExternalLink className="w-4 h-4 mr-2" /> {t("settings.billing.manageBilling")}</>)}
          </Button>
          <Button asChild variant="outline">
            <Link to="/billing/plans">{t("settings.billing.upgradePlan")}</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/pricing">{t("settings.billing.comparePlans")}</Link>
          </Button>
        </div>
      </CardContent>
    </Card>

    <InvoicesSection />
    <BillingSupportCard />
    </>
  );
}

function BillingSupportCard() {
  const { t } = useI18n();
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-primary" /> {t("settings.billing.support")}
        </CardTitle>
        <CardDescription>{t("settings.billing.supportDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <a href="mailto:jenny@adkavi.com?subject=Billing%20support%20request">
            <LifeBuoy className="w-4 h-4 mr-2" /> {t("settings.billing.contactSupport")}
          </a>
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">{t("settings.billing.supportFootnote")}</p>
      </CardContent>
    </Card>
  );
}

type Invoice = {
  id: string;
  number: string | null;
  created: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
};

const STATUS_OPTIONS = [
  { value: "all", tKey: "settings.inv.statusAll" },
  { value: "paid", tKey: "settings.inv.statusPaid" },
  { value: "open", tKey: "settings.inv.statusPending" },
  { value: "uncollectible", tKey: "settings.inv.statusFailed" },
  { value: "void", tKey: "settings.inv.statusVoided" },
  { value: "draft", tKey: "settings.inv.statusDraft" },
];

function InvoicesSection() {
  const { t, tn } = useI18n();
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [zipping, setZipping] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke("list-invoices", { body: { environment: getStripeEnvironment() } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setError(error.message);
        setInvoices((data?.invoices as Invoice[]) ?? []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = (invoices ?? []).filter((inv) => {
    if (statusFilter !== "all" && (inv.status ?? "") !== statusFilter) return false;
    const ts = inv.created * 1000;
    if (fromDate && ts < new Date(fromDate).getTime()) return false;
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      if (ts > end.getTime()) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const cents = inv.amount_paid || inv.amount_due;
      const amount = (cents / 100).toFixed(2);
      const dateStr = new Date(ts).toLocaleDateString().toLowerCase();
      const isoDate = new Date(ts).toISOString().slice(0, 10);
      const num = (inv.number ?? inv.id).toLowerCase();
      if (
        !num.includes(q) &&
        !amount.includes(q) &&
        !dateStr.includes(q) &&
        !isoDate.includes(q)
      ) return false;
    }
    return true;
  });

  const downloadAll = async () => {
    const withPdf = filtered.filter((i) => i.invoice_pdf);
    if (withPdf.length === 0) {
      toast.error(t("settings.toast.noInvoicePdfs"));
      return;
    }
    setZipping(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      await Promise.all(
        withPdf.map(async (inv) => {
          try {
            const res = await fetch(inv.invoice_pdf!);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const date = new Date(inv.created * 1000).toISOString().slice(0, 10);
            const name = `${date}_${inv.number ?? inv.id.slice(0, 10)}.pdf`;
            zip.file(name, blob);
          } catch (e) {
            console.error("Failed to fetch invoice", inv.id, e);
          }
        }),
      );
      const out = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(out);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adkavi-invoices-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(tn("settings.toast.invoicesDownloadedOne", "settings.toast.invoicesDownloadedMany", withPdf.length));
    } catch (e) {
      toast.error(t("settings.toast.zipFailed"), { description: (e as Error).message });
    } finally {
      setZipping(false);
    }
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error(t("settings.toast.noInvoicesExport"));
      return;
    }
    const escape = (v: string | number | null | undefined) => {
      const s = v === null || v === undefined ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const header = ["Date", "Invoice Number", "Amount", "Currency", "Status", "PDF URL"];
    const rows = filtered.map((inv) => {
      const cents = inv.amount_paid || inv.amount_due;
      // Force YYYY-MM-DD in UTC for consistency across timezones
      const d = new Date(inv.created * 1000);
      const date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
      return [
        date,
        inv.number ?? inv.id,
        (cents / 100).toFixed(2),
        inv.currency.toUpperCase(),
        inv.status ?? "",
        inv.invoice_pdf ?? inv.hosted_invoice_url ?? "",
      ].map(escape).join(",");
    });
    const csv = [header.map(escape).join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adkavi-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(tn("settings.toast.exportedCsvOne", "settings.toast.exportedCsvMany", filtered.length));
  };

  const hasFilters = statusFilter !== "all" || fromDate || toDate || searchQuery.trim() !== "";

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> {t("settings.inv.title")}
        </CardTitle>
        <CardDescription>{t("settings.inv.desc")}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("settings.inv.loading")}
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">{t("settings.inv.loadError").replace("{error}", error)}</p>
        ) : !invoices || invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            {t("settings.inv.empty")}
          </p>
        ) : (
          <>
            <div className="mb-3">
              <Label htmlFor="invoice-search" className="text-xs">{t("settings.inv.search")}</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="invoice-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("settings.inv.searchPlaceholder")}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="invoice-status" className="text-xs">{t("settings.inv.status")}</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="invoice-status" className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{t(o.tKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="invoice-from" className="text-xs">{t("settings.inv.from")}</Label>
                <Input
                  id="invoice-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-[150px]"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="invoice-to" className="text-xs">{t("settings.inv.to")}</Label>
                <Input
                  id="invoice-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-[150px]"
                />
              </div>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStatusFilter("all"); setFromDate(""); setToDate(""); }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={exportCsv}
                disabled={filtered.length === 0}
                className="ml-auto"
              >
                <Download className="w-4 h-4 mr-1" /> {t("settings.inv.exportCsv")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadAll}
                disabled={zipping || filtered.length === 0}
              >
                {zipping ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {t("settings.inv.zipping")}</>
                ) : (
                  <><Download className="w-4 h-4 mr-1" /> {t("settings.inv.downloadAll").replace("{n}", String(filtered.length))}</>
                )}
              </Button>
            </div>

            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">{t("settings.inv.noMatch")}</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-2 font-medium">{t("settings.inv.colDate")}</th>
                      <th className="text-left px-2 py-2 font-medium">{t("settings.inv.colInvoice")}</th>
                      <th className="text-right px-2 py-2 font-medium">{t("settings.inv.colAmount")}</th>
                      <th className="text-left px-2 py-2 font-medium">{t("settings.inv.colStatus")}</th>
                      <th className="text-right px-2 py-2 font-medium">{t("settings.inv.colPdf")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((inv) => {
                      const cents = inv.amount_paid || inv.amount_due;
                      const fmt = new Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: inv.currency.toUpperCase(),
                      });
                      return (
                        <tr key={inv.id} className="border-t border-border/40">
                          <td className="px-2 py-3 text-muted-foreground">
                            {new Date(inv.created * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </td>
                          <td className="px-2 py-3 font-mono text-xs">{inv.number ?? inv.id.slice(0, 10)}</td>
                          <td className="px-2 py-3 text-right tabular-nums">{fmt.format(cents / 100)}</td>
                          <td className="px-2 py-3">
                            <Badge
                              variant="outline"
                              className={
                                inv.status === "paid"
                                  ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                                  : inv.status === "open"
                                  ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
                                  : inv.status === "uncollectible"
                                  ? "border-destructive/40 text-destructive"
                                  : "border-border text-muted-foreground"
                              }
                            >
                              {inv.status ?? "—"}
                            </Badge>
                          </td>
                          <td className="px-2 py-3 text-right">
                            {inv.invoice_pdf ? (
                              <Button asChild variant="ghost" size="sm">
                                <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer">
                                  <ArrowDownToLine className="w-4 h-4 mr-1" /> {t("settings.inv.colPdf")}
                                </a>
                              </Button>
                            ) : inv.hosted_invoice_url ? (
                              <Button asChild variant="ghost" size="sm">
                                <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-4 h-4 mr-1" /> {t("settings.inv.colView")}
                                </a>
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ComplianceControlsPanel() {
  const { t } = useI18n();
  const [ageRestrictions, setAgeRestrictions] = useState(false);
  const [geoRestrictions, setGeoRestrictions] = useState(false);
  const [contentRestrictions, setContentRestrictions] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" /> {t("settings.controls.title")}
            </CardTitle>
            <CardDescription>
              {t("settings.controls.desc")}
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider shrink-0">
            <Clock className="h-3 w-3 mr-1" /> {t("settings.controls.preview")}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-foreground/90">
          {t("settings.controls.activateHint")}
        </div>

        <ComplianceRow
          title={t("settings.controls.ageTitle")}
          description={t("settings.controls.ageDesc")}
          checked={ageRestrictions}
          onChange={setAgeRestrictions}
        />
        <ComplianceRow
          title={t("settings.controls.geoTitle")}
          description={t("settings.controls.geoDesc")}
          checked={geoRestrictions}
          onChange={setGeoRestrictions}
        />
        <ComplianceRow
          title={t("settings.controls.contentTitle")}
          description={t("settings.controls.contentDesc")}
          checked={contentRestrictions}
          onChange={setContentRestrictions}
        />
      </CardContent>
    </Card>
  );
}

function ComplianceRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border/60 p-4">
      <div className="min-w-0">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={(v) => {
          onChange(v);
          if (v) {
            toast.info(t("settings.toast.savedPreference"), {
              description: t("settings.controls.effectHint"),
            });
          }
        }}
      />
    </div>
  );
}
